//#region libraries
import React, { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";

// external libraries
import { Subtitle, Title, Text } from "@tremor/react";
import { Flex, DateRangePicker, Toggle, ToggleItem } from "@tremor/react";


// custom components
import { CustomSpinner, BookingRequestSearchBar, Card } from "_atoms";
import { TableHeader, TableValue, NewAssistanceColumn, PRMBIO, AirportSearch } from "_molecules";
import { BookingRequestsTable, ProfileBubble } from "_organisms";

// bootstrap
import { Button, Container, Row, Col, Modal, Form } from "react-bootstrap";
import moment from 'moment';

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUserDb, selectIata, sendEmail, selectSendEmailStatus, resetSendEmailStatus } from "_redux/slices/main-screen/user";
import {
  getConfirmedAirlines,
  selectConfirmedAirlines,
  selectAirlineRequests,
  getAirlineRequests,
  selectConfirmedArrivals,
  selectConfirmedDepartures,
  getConfirmedArrivals,
  getConfirmedDepartures,
  selectDeleteStatus,
  deleteRequest,
  patchRequests,
  clearList,
  selectPatchStatus,
  selectSelectedRequests,
  selectRequestsDepartures,
  selectRequestsArrivals,
  getDepartures,
  getArrivals,
  reset,
  setEditBookingStatus,
  selectEditBookingStatus,
  setType as setTypeTable,
  selectUserInfo,
  getUserInfo,
  selectTotalRows
} from "_redux/slices/assistance-bookings/booking-requests-table";
import { selectBookingForm, reset as resetForm } from "_redux/slices/assistance-bookings/booking-form";
import { patchBooking, selectPatchBookingStatus, selectDeleteStatus as selectEditDeleteStatus } from "_redux/slices/prm-bookings/bookings";

// styles
import { Colors } from "_styles";

import * as BookingService from "_services/booking-service";

// firebase
import { getAnalytics, logEvent } from "firebase/analytics";

import { lookupIsLoading } from "_utils/statusUtil";
import Pagination from "./pagination";
//#endregion



function searchMatch(doc, text) {

  if (!text) {
    return true;
  }
  const name = doc.name.toLowerCase();
  return name?.includes(text.toLowerCase())
}


function getDateOneMonthAgo(currentDate) {
  // Get the current timestamp
  const currentTimestamp = currentDate.getTime();

  // Calculate the timestamp for 30 days ago (30 * 24 * 60 * 60 * 1000 milliseconds)
  const thirtyDaysAgoTimestamp = currentTimestamp - 2592000000;

  // Create a new date object from the timestamp for 30 days ago
  const thirtyDaysAgo = new Date(thirtyDaysAgoTimestamp);

  return thirtyDaysAgo;
}

// Ensure the function returns a valid two-element array
const getDateOneWeek = (date, direction = 1) => {
  const result = new Date(date);
  result.setDate(result.getDate() + (7 * direction));
  return result;
};


const getDateOneMonth = (currentDate, monthsToAdd = 1) => {
  const resultDate = new Date(currentDate);
  resultDate.setMonth(resultDate.getMonth() + monthsToAdd);
  return resultDate;
};

const BookingRequests = () => {
  //#region redux
  const dispatch = useDispatch();
  // firebase
  const analytics = getAnalytics();
  const userDb = useSelector(selectUserDb);
  const airlineRequests = useSelector(selectAirlineRequests);
  const arrivalsList = useSelector(selectRequestsArrivals);
  const departuresList = useSelector(selectRequestsDepartures);
  const confirmedArrivals = useSelector(selectConfirmedArrivals)
  const confirmedDepartures = useSelector(selectConfirmedDepartures);
  const confirmedAirlines = useSelector(selectConfirmedAirlines);
  const selectedRequests = useSelector(selectSelectedRequests);
  const bookingForm = useSelector(selectBookingForm);
  const iata = useSelector(selectIata);
  const editStatus = useSelector(selectPatchBookingStatus);
  const deleteEditStatus = useSelector(selectEditDeleteStatus);
  const editBookingStatus = useSelector(selectEditBookingStatus); // bool shows edit booking form
  const emailStatus = useSelector(selectSendEmailStatus);
  const userInfo = useSelector(selectUserInfo);

  // status
  const patchStatus = useSelector(selectPatchStatus);
  const deleteStatus = useSelector(selectDeleteStatus);
  const totalRows = useSelector(selectTotalRows);
  //#endregion

  //#region state
  // filters
  const today = new Date();
  const [dateRange, setDateRange] = useState([today, getDateOneMonth(today), "Next 30 days"]);
  const [searchConfirmed, setSearchConfirmed] = useState("");
  const [airport, setAirport] = useState("");
  const [iataForSearch, setIataForSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(100);
  const [allUnconfirmRequests, setAlUnconfirmRequests] = useState(false)
  const [allUnformedStartDate, setAllUnformedStartDate] = useState(new Date())
  const [deletedRequest, setDeletedRequest] = useState(null);// the selected for deletion

  const [type, setType] = useState("departures"); // type of lis to show
  const [confirmedType, setConfirmedType] = useState("arrivals"); // filter confirmed table by arrival/departure
  const [deleteModal, setDeleteModal] = useState(false);
  const [emailNotes, setEmailNotes] = useState("");
  const [callingDelete, setCallingDelete] = useState(false);
  const [portalBooking, setPortalBooking] = useState(false);


  const [modal, setModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [modalButtonType, setModalButtonType] = useState("primary-outline");
  const [modalButtonTitle, setModalButtonTitle] = useState("");
  const [tableData, setTableData] = useState([])
  //#endregion

  //#region loader selector
  const graphDataServiceFunction =
    userDb?.details?.type == "AIRLINE" && type == "requests"
      ? "getAirlineRequests"
      : type == "departures"
        ? "getDeparturesList"
        : type == "arrivals"
          ? "getArrivalsList"
          : type == "confirmed" && userDb?.details?.type == "AIRLINE"
            ? "getConfirmedAirlines"
            : type == "confirmed" && confirmedType == "arrivals"
              ? "getConfirmedArrivals"
              : "getConfirmedDepartures";


  const graphDataIsLoading = useSelector((state) => lookupIsLoading(state.requests.statusObj, [graphDataServiceFunction]));
  //#endregion

  //#region date picker
  const customPresets = [
    { text: "Today", value: "Today", startDate: today, endDate: today },
    { text: "Next 7 days", value: "Next 7 days", startDate: today, endDate: getDateOneWeek(today) },
    { text: "Next 30 days", value: "Next 30 days", startDate: today, endDate: getDateOneMonth(today) },
    { text: "Last 7 days", value: "Last 7 days", startDate: getDateOneWeek(today, -1), endDate: today },
    { text: "Last 30 days", value: "Last 30 days", startDate: getDateOneMonth(today, -1), endDate: today }
  ];

  // Function to handle date changes
  const editDate = (selectedDate) => {

    let newRange = [];
    setDateRange(selectedDate)
    // // Case 1: User clicked a preset (selectedDate?.[2] holds the actual date range)
    // const potentialRange = selectedDate?.[2];
    // if (Array.isArray(potentialRange) && potentialRange.length === 2) {
    //   newRange = potentialRange;
    // }
    // // Case 2: User manually clicked 1 or 2 dates in the calendar
    // else if (Array.isArray(selectedDate)) {
    //   newRange = selectedDate.flat().filter(date => date !== null);

    //   // Handle single click (waiting for second date)
    //   if (newRange.length === 1) {
    //     const [clickedDate] = newRange;
    //     const [existingStart] = dateRange;

    //     newRange = [
    //       clickedDate < existingStart ? clickedDate : existingStart,
    //       clickedDate < existingStart ? existingStart : clickedDate
    //     ];
    //   }
    //   // Handle two or more selected dates
    //   else if (newRange.length >= 2) {
    //     newRange = newRange.slice(0, 2).sort((a, b) => a - b);
    //   }
    // }

    // // Ensure a valid range
    // if (newRange.length !== 2) return;

    // // Update state
    // setDateRange(newRange);

    // // Match with preset
    // const matchedPreset = customPresets.find(
    //   (preset) =>
    //     preset.value[0].toISOString().split("T")[0] === newRange[0].toISOString().split("T")[0] &&
    //     preset.value[1].toISOString().split("T")[0] === newRange[1].toISOString().split("T")[0]
    // );

    // setSelectedPreset(matchedPreset ? matchedPreset.text : "Select Date");
  };

  //#region useEffect
  useEffect(() => {
    if (userDb.details.type == "AIRLINE") {
      // log airline event
      logEvent(analytics, "request-table-view", {
        airline: userDb.details.authority,
      });
    }
    if (userDb.details.type == "AIRPORT") {
      // log airline event
      logEvent(analytics, "request-table-view", {
        airport: userDb.details.authority,
      });
    }
  }, []);

  useEffect(() => {
    if (userDb?.details?.type == "AIRLINE") {
      setType("requests");
    }
  }, []);

  useEffect(() => {
    // update date string on date change
    dispatch(resetSendEmailStatus());
  }, []);

  useEffect(() => {
    if (userDb?.details?.type == "AIRLINE") {
      setType("requests");
    }
  }, []);

  useEffect(() => {
    // got user info for email
    if (userInfo && callingDelete) {
      dispatch(
        sendEmail({
          email: userInfo.email,
          subject: "Booking Request Denied",
          body: emailNotes || "Booking request has been denied",
          booking: deletedRequest
        })
      );
      setCallingDelete(false);
    }
  }, [userInfo, callingDelete]);



  useEffect(() => {
    const graphData =
      userDb?.details?.type == "AIRLINE" && type == "requests"
        ? airlineRequests
        : type == "departures"
          ? departuresList
          : type == "arrivals"
            ? arrivalsList
            : type == "confirmed" && userDb?.details?.type == "AIRLINE"
              ? confirmedAirlines
              : type == "confirmed" && confirmedType == "arrivals"
                ? confirmedArrivals
                : confirmedDepartures;

    if (searchConfirmed && searchConfirmed.length > 3) {
      const tempArray = graphData.filter(doc => searchMatch(doc, searchConfirmed))
      setTableData(tempArray)
    } else {
      setTableData(graphData)
    }

  }, [userDb, type, airlineRequests, departuresList, arrivalsList, confirmedArrivals, confirmedDepartures, searchConfirmed]);

  // email status
  useEffect(() => {

    // email successfully sent
    if (emailStatus == "succeeded") {
      // complete deleting booking
      deleteBooking();
    }
    // email failed
    if (emailStatus == "failed") {
      setDeleteModal(false);
      openModal("failed");
    }
  }, [emailStatus]);


  const patchBookings = () => {
    let requestedBookings = [];

    if (selectedRequests.length == 0) {
      openModal("minimum");
      return;
    }

    if (selectedRequests.length > 99) {
      openModal("maximum");
      return;
    }

    selectedRequests.forEach((index) => {
      requestedBookings.push(tableData[index]["_id"]);
    });


    let requestType = "outbound";
    if (type == "arrivals") {
      requestType = "inbound";
    }

    if (userDb.details.type == "AIRLINE") {
      dispatch(
        patchRequests({
          requests: { data: requestedBookings },
          type: "airline",
        })
      );
    } else {
      dispatch(
        patchRequests({
          requests: { data: requestedBookings },
          type: requestType,
        })
      );
    }
  };

  const updateBooking = () => {
    dispatch(patchBooking(bookingForm));
  };

  const openDeleteModal = () => {
    // find booking
    let booking = ''
    if (type == 'arrivals') { booking = arrivalsList[selectedRequests[0]] }
    else if (type == 'departures') { booking = (departuresList[selectedRequests[0]]) }
    else { booking = airlineRequests[selectedRequests[0]] }
    if (!booking) return;

    if (booking.portal !== "APP") {
      setPortalBooking(true)
      setDeleteModal(true)
    } else {
      dispatch(getUserInfo(booking.doc.userId))
      setPortalBooking(false)
      setDeleteModal(true)
    }
  }
  //#endregion


  //#region functions
  // get email for user deletion
  const getDeleteEmail = () => {


    let booking = "";
    if (type == "arrivals") {
      booking = arrivalsList[selectedRequests[0]];
    } else if (type == "departures") {
      booking = departuresList[selectedRequests[0]];
    } else {
      booking = airlineRequests[selectedRequests[0]];
    }
    if (booking.portal !== "APP") {
      deleteBooking();
    } else {
      setDeletedRequest(booking.doc)
      setCallingDelete(true);
      dispatch(getUserInfo(booking.doc.userId)); // get user email
    }

  };

  const deleteBooking = () => {

    if (selectedRequests.length > 0) {
      openModal("delete-failed");
    }
    if (type == "arrivals") {
      dispatch(deleteRequest(arrivalsList[selectedRequests[0]]));
    } else if (type == "departures") {
      dispatch(deleteRequest(departuresList[selectedRequests[0]]));
    } else {
      dispatch(deleteRequest(airlineRequests[selectedRequests[0]]));
    }
    setDeletedRequest(null)
    dispatch(resetSendEmailStatus())
  };
  //#endregion

  //#region modals
  // handle modal
  const openModal = (type) => {
    setDeleteModal(false);
    if (type == "failed") {
      setModalTitle("Issue updating bookings");
      setModalDescription(
        "There was a problem updating the booking requests, please try again."
      );
      setModalButtonType("outline-danger");
      setModalButtonTitle("Close");
      setModal(true);
    }
    if (type == "delete-failed") {
      setModalTitle("Issue Denying Multiple Request");
      setModalDescription(
        "Unfortunately, you can only deny one booking at a time."
      );
      setModalButtonType("outline-primary");
      setModalButtonTitle("Close");
      setModal(true);
    }
    if (type == "succeeded") {
      setModalTitle("Successfully updated your requests");
      setModalDescription(
        "Your bookings have succesfully been updated and stored."
      );
      setModalButtonType("outline-primary");
      setModalButtonTitle("Done");
      setModal(true);
    }
    if (type == "minimum") {
      setModalTitle("Issue - Minimum One Booking");
      setModalDescription(
        "In order to perform an approve or deny action on a request you must select at least one"
      );
      setModalButtonType("outline-primary");
      setModalButtonTitle("Done");
      setModal(true);
    }
    if (type == "maximum") {
      setModalTitle("Issue - Maximum 100 Requests");
      setModalDescription(
        "There is a limit of 100 requests per approval. Please unselect some bookings before approving again."
      );
      setModalButtonType("outline-primary");
      setModalButtonTitle("Done");
      setModal(true);
    }
  };

  // handle modal close
  const closeModal = () => {
    dispatch(reset());
    setIataForSearch("")
    setAirport("")
    setDateRange([today, getDateOneMonth(today), "Next 30 days"]);

    if (dateRange[0] && dateRange[1]) {

      const startDate = moment(new Date(dateRange[0])).format().split("T")[0],
        endDate = moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0];

      if (type === "departures") {
        dispatch(
          getDepartures({
            type: "outbound",
            iata: iata ? iata : userDb.details.authority,
            startDate: allUnconfirmRequests ? allUnformedStartDate : startDate,
            endDate: allUnconfirmRequests ? undefined : endDate
          })
        );
      }

      if (type === "arrivals") {
        dispatch(
          getArrivals({
            type: "inbound",
            iata: iata ? iata : userDb.details.authority,
            limit,
            skip,
            startDate: allUnconfirmRequests ? allUnformedStartDate : startDate,
            endDate: allUnconfirmRequests ? undefined : endDate
          })
        );
      }

      if (type === "confirmed" && confirmedType === "departures") {
        dispatch(
          getConfirmedDepartures({
            type: "outbound",
            iata: iata ? iata : userDb.details.authority,
            limit,
            skip,
            startDate,
            endDate,
          })
        );
      }

      if (type === "confirmed" && confirmedType === "arrivals") {
        dispatch(
          getConfirmedArrivals({
            type: "inbound",
            iata: iata ? iata : userDb.details.authority,
            limit,
            skip,
            startDate,
            endDate,
          })
        );
      }

      if (userDb.details.type === "AIRLINE") {
        const params = {
          iata: iata ? iata : userDb.details.authority,
          airline: userDb.details.authority === "ALL" ? "ALL" : true,
          confirmed: false,
          csv: false,
          limit,
          skip,
          startDate,
          endDate,
        }
        dispatch(getAirlineRequests(params));
      }

      if (type === "confirmed" && userDb.details.type === "AIRLINE") {

        dispatch(
          getConfirmedAirlines({
            type: "airline",
            iata: iata ? iata : userDb.details.authority,
            airline: userDb.details.authority === "ALL" ? "ALL" : true,
            limit,
            skip,
            startDate,
            endDate,
          })
        );

      }

      setModal(false);
    }
  };



  // modal handler using status //
  useEffect(() => {
    if (
      patchStatus == "failed" ||
      deleteStatus == "failed" ||
      deleteEditStatus == "failed" ||
      editStatus == "failed"
    ) {
      openModal("failed");

    }
    if (patchStatus == "succeeded") {
      // log booking confirmed
      if (userDb.details.type == "AIRLINE") {
        // log airline event
        logEvent(analytics, "request-table-booking-confirmed", {
          airline: userDb.details.authority,
        });
      }
      if (userDb.details.type == "AIRPORT") {
        // log airline event
        logEvent(analytics, "request-table-booking-confirmed", {
          airport: userDb.details.authority,
        });
      }
    }
    if (deleteStatus == "succeeded") {
      // log booking confirmed
      if (userDb.details.type == "AIRLINE") {
        // log airline event
        logEvent(analytics, "request-table-booking-deleted", {
          airline: userDb.details.authority,
        });
      }
      if (userDb.details.type == "AIRPORT") {
        // log airline event
        logEvent(analytics, "request-table-booking-deleted", {
          airport: userDb.details.authority,
        });
      }
    }
    if (
      patchStatus == "succeeded" ||
      deleteStatus == "succeeded" ||
      deleteEditStatus == "succeeded" ||
      editStatus == "succeeded"
    ) {
      openModal("succeeded");
    }
  }, [patchStatus, deleteStatus, deleteEditStatus, editStatus]);


  //#region getting requests/confirmed rows
  // reset and get requests
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = moment(new Date(dateRange[0])).format().split("T")[0],
        endDate = moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0];

      if (type === "confirmed" && confirmedType === "departures" && userDb.details.type !== "AIRLINE") {
        dispatch(
          getConfirmedDepartures({
            type: "outbound",
            iata: iata ? iata : userDb.details.authority,
            startDate,
            endDate,
            limit,
            skip,

          })
        );
      }

      if (type === "confirmed" && confirmedType === "arrivals" && userDb.details.type !== "AIRLINE") {
        dispatch(
          getConfirmedArrivals({
            type: "inbound",
            iata: iata ? iata : userDb.details.authority,
            startDate,
            endDate,
            limit,
            skip,
          })
        );
      }
      if (userDb.details.type === "AIRLINE" && type === "confirmed") {
        dispatch(
          getConfirmedAirlines({
            airline: userDb.details.authority === "ALL" ? "ALL" : true,
            iata: iata ? iata : userDb.details.authority,
            airport: iataForSearch,
            startDate,
            endDate,
            limit,
            skip,
            type: "airline"
          })
        );
      }

      if (type === "departures" && userDb.details.type !== "AIRLINE") {
        dispatch(
          getDepartures({
            type: "outbound",
            iata: iata ? iata : userDb.details.authority,
            startDate: allUnconfirmRequests ? allUnformedStartDate : startDate,
            endDate: allUnconfirmRequests ? undefined : endDate,
            limit,
            skip
          })
        );

      }
      if (type === "arrivals" && userDb.details.type !== "AIRLINE") {
        dispatch(
          getArrivals({
            type: "inbound",
            iata: iata ? iata : userDb.details.authority,
            limit,
            skip,
            startDate: allUnconfirmRequests ? allUnformedStartDate : startDate,
            endDate: allUnconfirmRequests ? undefined : endDate
          })
        );
      }
      if (userDb.details.type === "AIRLINE" && type === "requests") {

        dispatch(
          getAirlineRequests({
            iata: iata ? iata : userDb.details.authority,
            airline: userDb.details.authority === "ALL" ? "ALL" : true,
            confirmed: false,
            csv: false,
            airport: iataForSearch,
            limit,
            skip,
            startDate,
            endDate,
          })
        ); // get airline requests
      }
    }
    // reset skip
    // dispatch(
    //   bookingsRequestTableSlice.resetSkip(
    //     translateType(type, confirmedType, userDb)
    //   )
    // );
  }, [dateRange, type, confirmedType, limit, skip, iataForSearch]);
  //#endregion

  //#endregion


  useEffect(() => {
    dispatch(clearList());
  }, [type]);

  async function downloadCSVfile() {

    const airlineParams = {
      iata: userDb.details.authority,
      airline: userDb.details.authority === "ALL" ? "ALL" : true,
      csv: true,
      confirmed: type == "confirmed",
      airport: iataForSearch,
      startDate: moment(new Date(dateRange[0])).format().split("T")[0],
      endDate: moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0],
    }
    const airportParams = {
      type: type == "arrivals" ? "inbound" : "outbound",
      iata: iata ? iata : userDb.details.authority,
      csv: true,
      confirmed: type == "confirmed",
      startDate: moment(new Date(dateRange[0])).format().split("T")[0],
      endDate: moment(new Date(dateRange[1] ? dateRange[1] : dateRange[0])).format().split("T")[0],
    }
    const param = userDb.details.type == "AIRLINE" ? airlineParams : airportParams;

    let csv = null;
    if (userDb.details.type == "AIRLINE") {
      csv = await BookingService.getAirlineRequests(param).catch((e) => console.log("error exporting csv"));

    } else {
      csv = await BookingService.getRequests(param).catch((e) => console.log("error exporting csv"));

    }
    const element = document.createElement("a");
    const file = new Blob([csv], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "requested_bookings_export.csv";
    document.body.appendChild(element);
    element.click();
  }

  // columns for table
  const columns = useMemo(
    () => [
      {
        Header: <TableHeader title={"Name"} />,
        accessor: "name",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Flight"} />,
        accessor: "flightNumber",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Reference"} />,
        accessor: "referenceNumber",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Arrival"} />,
        accessor: "arrivalTime",
        Cell: ({ cell: { value } }) => (
          <TableValue
            title={
              !isNaN(new Date(value).getTime())
                ? format(new Date(value), "h:mm a")
                : "N/A"
            }
          />
        ),
      },
      {
        Header: <TableHeader title={"Departure"} />,
        accessor: "departureTime",
        Cell: ({ cell: { value } }) => (
          <TableValue
            title={
              !isNaN(new Date(value).getTime())
                ? format(new Date(value), "h:mm a")
                : "N/A"
            }
          />
        ),
      },
      {
        Header: <TableHeader title={"Date"} />,
        accessor: "departureDate",
        Cell: ({ cell: { value } }) => <TableValue title={value} />,
      },
      {
        Header: <TableHeader title={"Assistance"} />,
        accessor: "assistances",
        // render custom object
        Cell: ({ cell: { value } }) => <NewAssistanceColumn values={value} />,
      },
      {
        Header: <TableHeader title={"Notes"} />,
        accessor: "bio",
        // render custom object
        Cell: ({ cell: { value } }) => (
          <div>
            <PRMBIO title={value} comment={true} />
          </div>
        ),
      },
      {
        Header: <TableHeader title={"Time Created"} />,
        accessor: "dateCreated",
        Cell: ({ cell: { value } }) => {
          if (new Date(value) != "Invalid Date") {
            return (
              <p style={{ opacity: 0.4, marginLeft: 10, marginTop: 5 }}>
                {new Date(value).toLocaleTimeString().substring(0, 5) +
                  " " +
                  new Date(value).toLocaleDateString()}
              </p>
            );
          } else {
            return null;
          }
        },
      },
      {
        Header: <TableHeader title={"Type"} />,
        accessor: "portal",
        Cell: ({ cell: { value } }) => {
          const display = value?.startsWith("Portal_") ? "Portal" : (value || "APP");
          return <TableValue title={display} />;
        },
      },
      {
        Header: <TableHeader title={"Owner"} />,
        id: "owner",
        Cell: ({ row }) => {
          const portal = row.original.portal || "";
          const owner = portal.startsWith("Portal_") ? portal.replace("Portal_", "") : "—";
          return <TableValue title={owner} />;
        },
      },
      ...(type === "confirmed" ? [
        {
          Header: <TableHeader title={"Confirm Time"} />,
          id: "confirmTime",
          Cell: ({ row }) => {
            const doc = row.original.doc;
            let confirmTime;
            if (userDb?.details?.type === "AIRLINE") {
              confirmTime = doc?.airlineConfirmedTime || (doc?.airlineConfirmed ? doc?.dateCreated : null);
            } else if (confirmedType === "arrivals") {
              confirmTime = doc?.destinationConfirmedTime || (doc?.destinationConfirmed ? doc?.dateCreated : null);
            } else {
              confirmTime = doc?.originConfirmedTime || (doc?.originConfirmed ? doc?.dateCreated : null);
            }
            if (!confirmTime || isNaN(new Date(confirmTime).getTime())) {
              return <p style={{ opacity: 0.4, marginLeft: 10, marginTop: 5 }}>N/A</p>;
            }
            const d = new Date(confirmTime);
            return (
              <p style={{ opacity: 0.4, marginLeft: 10, marginTop: 5 }}>
                {d.toLocaleTimeString().substring(0, 5) + " " + d.toLocaleDateString()}
              </p>
            );
          },
        },
      ] : []),
    ],
    [type, confirmedType, userDb]
  );

  //skip next rows (limit 10,25,50...)
  const showMore = () => {
    setSkip(skip + limit)
  }
  //skip to previous rows (limit 10,25,50...)
  const showLess = () => {
    setSkip(skip - limit)
  }
  //chnage number of rows per page
  const handleLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
  }

  const handleAllRequests = (value) => {
    setAlUnconfirmRequests(value);
    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(startDate.getMonth() - 1);

    // Format as YYYY-MM-DD
    const formattedStartDate = startDate.toISOString().split("T")[0];
    setAllUnformedStartDate(formattedStartDate)
    if (!value) return;
    if (type === "departures") {
      dispatch(
        getDepartures({
          type: "outbound",
          iata: iata ? iata : userDb.details.authority,
          startDate: formattedStartDate,
          // endDate,
          limit: 1000,
          skip: 0
        })
      );
    }
    if (type === "arrivals") {
      dispatch(
        getArrivals({
          type: "inbound",
          iata: iata ? iata : userDb.details.authority,
          limit: 1000,
          skip: 0,
          startDate: formattedStartDate,
          // endDate,
        })
      );
    }
  }


  return (
    <div>
      {!editBookingStatus && (
        <div>
          {/* search bar and filter */}
          <Row className="mt-3">
            <Col>
              <BookingRequestSearchBar
                setSearchConfirmed={setSearchConfirmed}
                type={type}
                confirmedType={confirmedType}
              />
            </Col>
            <Col>
              {userDb.details.type === "AIRPORT" && <Form.Check
                onChange={(e) => { handleAllRequests(e.target.checked) }}
                type="checkbox"
                label="All Requests"
                defaultChecked={false}
                checked={allUnconfirmRequests}
              />}
            </Col>
            <Col>
              <Flex>
                <DateRangePicker
                  onValueChange={editDate}
                  value={dateRange}
                  enableDropdown={true}
                  maxWidth="max-w-lg"
                  // dropdownPlaceholder={selectedPreset}
                  options={customPresets}
                />
              </Flex>

            </Col>
            {userDb.details.authority === "ALL" ?
              <Col>
                <AirportSearch type={'airport'} setSelection={setIataForSearch} setAirport={setAirport} />
              </Col>
              : <></>}
          </Row>
        </div>
      )}

      {/* Booking Type and Buttons */}
      <Row style={{}}>
        <Col xs={editBookingStatus ? 8 : 7}>
          {editBookingStatus && <ProfileBubble />}
        </Col>
      </Row>

      <div>
        {graphDataIsLoading ? (
          <Row
            style={{ opacity: 0.6 }}
            className="d-flex justify-content-center mt-5"
          >

            <p className="mt-3" style={{ textAlign: "center", color: Colors.PRM_LIGHT_BLUE }}            >
              {"Getting Booking Requests"}
            </p>
          </Row>
        ) : (
          /* table */
          <div className="mt-3" style={{ paddingBottom: 22 }}>
            <Card>
              {/* table title + arrivals / departures filter */}
              <Flex>
                <div>
                  <Title>Request Table</Title>
                  <Subtitle>
                    View booking requests from passengers, approve or deny
                    requests and view confirmed bookings.
                  </Subtitle>
                </div>

                {/* arrivals / departures filter */}
                <div>
                  <Toggle
                    color="zinc"
                    defaultValue={type}
                    onValueChange={(value) => setType(value)}
                  >
                    {userDb?.details?.type !== "AIRLINE" ? (
                      <>
                        <ToggleItem value={"arrivals"} text="Arrivals" />
                        <ToggleItem value={"departures"} text="Departures" />
                      </>
                    ) : (
                      <ToggleItem value={"requests"} text="requests" />
                    )}
                    <ToggleItem value={"confirmed"} text="Confirmed Requests" />
                  </Toggle>
                </div>
              </Flex>

              {/* confirm + export buttons */}
              <div className="mt-3">
                <Flex>

                  <div style={{}}>
                    {editBookingStatus == true ? (
                      <Row>
                        <div class="col"></div>
                        <div class="col-12 col-sm-auto">
                          <Button
                            variant={"outline-primary"}
                            onClick={updateBooking}
                            loading={true}
                            style={{ width: 150 }}
                          >
                            {" "}
                            {editStatus == "loading" ? (
                              <CustomSpinner />
                            ) : (
                              "Update Booking"
                            )}{" "}
                          </Button>
                          <Button
                            variant={"outline-secondary"}
                            onClick={() => {
                              dispatch(resetForm());
                              dispatch(setEditBookingStatus(false));
                            }}
                            loading={true}
                            style={{ width: 150, marginLeft: 15 }}
                          >
                            {deleteStatus == "loading" ? (
                              <CustomSpinner />
                            ) : (
                              "Cancel"
                            )}
                          </Button>
                        </div>
                      </Row>
                    ) : type != "confirmed" ? (
                      <Flex>
                        <Button
                          disabled={selectedRequests?.length > 0 ? false : true}
                          variant={"outline-primary"}
                          onClick={patchBookings}
                          loading={true}
                          style={{ width: 150, marginInlineEnd: "20px" }}
                        >
                          {patchStatus == "loading" ? (<CustomSpinner />) : ("Confirm")}
                        </Button>

                        <Button variant={"outline-danger"} disabled={selectedRequests?.length > 0 ? false : true} onClick={openDeleteModal} loading={true} style={{ width: 150, marginLeft: 15 }}>{deleteStatus == 'loading' ? <CustomSpinner /> : 'Deny'}</Button>

                      </Flex>
                    ) :
                      userDb && userDb.details.type == "AIRLINE" ? null : (
                        <Col>
                          <Button
                            variant={
                              confirmedType == "departures"
                                ? "primary"
                                : "outline-primary"
                            }
                            onClick={() => {
                              setConfirmedType("departures");
                            }}
                            loading={true}
                            style={{ width: 150 }}
                          >
                            {patchStatus == "loading" ? (
                              <CustomSpinner />
                            ) : (
                              "Confirmed Departures"
                            )}
                          </Button>
                          <Button
                            variant={
                              confirmedType == "arrivals"
                                ? "primary"
                                : "outline-primary"
                            }
                            onClick={() => {
                              setConfirmedType("arrivals");
                            }}
                            loading={true}
                            style={{ width: 150, marginLeft: 15 }}
                          >
                            {deleteStatus == "loading" ? (
                              <CustomSpinner />
                            ) : (
                              "Confirmed Arrivals"
                            )}
                          </Button>
                        </Col>
                      )}
                  </div>
                </Flex>
              </div>

              <div
                style={{
                  "overflow-y": "scroll",
                  height: "40em",
                  display: "block",
                }}
              >

                <BookingRequestsTable
                  columns={columns}
                  type={type}
                  data={tableData?.length ? tableData : []}
                  confirmedType={confirmedType}
                />
              </div>

              <Flex>
                {/* export csv button */}
                <Button
                  variant={"outline-secondary"}
                  onClick={downloadCSVfile}
                  loading={true}
                  style={{ width: 150, marginLeft: 15 }}
                >
                  {deleteStatus == "loading" ? <CustomSpinner /> : "Export"}
                </Button>

                {/* pagination */}
                <Row className="mt-2">
                  <div style={{ float: "inline-end", marginRight: "40px" }}>
                    <Pagination
                      showLess={showLess}
                      showMore={showMore}
                      limit={limit}
                      skippedRows={skip}
                      totalRows={totalRows}
                      handleLimitChange={handleLimitChange}
                    />
                  </div>
                </Row>
              </Flex>
            </Card>
          </div>
        )}
      </div>

      <Modal show={modal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>{modalDescription}</Modal.Body>

        <Modal.Footer>
          <Button variant={modalButtonType} onClick={closeModal}>
            {modalButtonTitle}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteModal}
        onHide={() => { setDeleteModal(false) }}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Booking</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteStatus == "loading" || emailStatus == "loading" ? (<CustomSpinner />) : (
            <>
              <Text>
                The passenger will be emailed that their booking has been
                deleted.
                {portalBooking
                  ? " This booking was made on the PRM Assist portal and the passenger cannot be notified via email"
                  : " If necessary, include a message to be attached to the email."}
              </Text>

              {portalBooking ? (
                " "
              ) : (
                <Container style={{ paddingBottom: 12 }}>
                  <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                    Notes (Optional)
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    style={{ height: 200 }}
                    onChange={(e) => {
                      setEmailNotes(e.target.value);
                    }}
                    type="text"
                    placeholder="Any additional information to be sent to the passenger"
                    value={emailNotes}
                  />
                </Container>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant={"outline-danger"} onClick={getDeleteEmail}>
            Delete Booking
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BookingRequests;